import carouselActions from '../actions/carouselActions';
import chatbotActions from '../actions/chatbotActions';
import vtHelpfulnessActions from '../actions/vtHelpfulnessActions';
import contactOwnerFormActions from '../actions/contactOwnerFormActions';
import datePickerActions from '../actions/datePickerActions';
import edapActions from '../actions/edapActions';
import guestSelectorActions from '../actions/guestSelectorActions';
import inquiryModalActions from '../actions/inquiryModalActions';
import listingActions from '../actions/listingActions';
import listingExtendedActions from '../actions/listingExtendedActions';
import listingPageActions from '../actions/listingPageActions';
import navigationActions from '../actions/navigationActions';
import requestToBookModalActions from '../actions/requestToBookModalActions';
import reviewsActions from '../actions/reviewsActions';
import routerActions from '../actions/routerActions';
import travelerActions from '../actions/travelerActions';
import tripActions from '../actions/tripActions';
import windowActions from '../actions/windowActions';
import {registerMiddleware} from '@homeaway/redux-middleware-adapter';
import {logToServer} from './logger';
import {logPrimaryActionRendered} from './utils/logPrimaryActionRendered';
import {selectNextImage, selectPrevImage} from './carousel';
import {requestToBookModalSubmit} from './requestToBookModal';
import {
    viewAllReviews,
    previousPage,
    nextPage,
    translateReviews,
    showOriginalReviews
} from './reviews';
import {inquirySubmit, inquiryModalRequestToBook, inquiryPostApplink} from './inquiryModal';
import {chatbotRequestToBook, triggerChatExitToast} from './chatbot';
import updateLodgingCancellationPolicyIfArrivalDateChanged from './lodgingCancellationPolicy';
import {
    triggerEdapEvent,
    triggerEdapListingBooknowClick,
    triggerEdapCheckoutSelected,
    triggerEdapPropertySummaryPresented,
    triggerEdapPriceQuotePresented,
    logTestExposure
} from './edap';
import {tripDetailsChanged, tripDetailsEdapCheck} from './trip';
import {
    closeDatePickerModal,
    openDatePickerModal,
    selectedDatesSubmit,
    changedSelectedDates
} from './datePicker';
import {
    guestSelectorChanged,
    openGuestSelectorModal,
    closeGuestSelectorModal,
    submitGuestSelectorModal
} from './guestSelector';
import {
    civilCodeDisclaimerClicked,
    setCivilCodeDisclaimerModalOpen,
    setRequestToBookModalOpen,
    setInquiryModalOpen,
    setLoginModalOpen,
    setImageCarouselModalOpen,
    setInquiryLoginModalOpen,
    setVirtualTourModalOpen,
    setDescriptionModalOpen,
    setUnitDescriptionModalOpen,
    setGeneratedDescriptionModalOpen,
    setAmenityModalOpen,
    setOwnerProfileModalOpen,
    pageLoadedFetchRegistrationNumber,
    pageLoadedFetchGeoDistance,
    processServerSideEvaluatedTests,
    triggerUrgencyToast,
    updateTripDataCookie,
    setLocationModalOpen,
    setMapModalOpen,
    setReviewsModalOpen,
    setSearchModalOpen,
    setSearchDatePickerModalOpen,
    setSearchGuestPickerModalOpen,
    viewPriceDetailsClicked,
    inquiryClicked,
    navClicked,
    logPriceSummaryEvent,
    fetchPropertyDescriptionLocalized,
    setVerticalGalleryModalOpen,
    setToiModalOpen,
    triggerVTExitToast
} from './listingPage';
import {
    fetchByOwnerProperties,
    fetchRecommendedProperties,
    fetchInquiryProperties,
    fetchUnitSpaces,
    fetchUrgencyMessage,
    fetchDiscoveryFeed,
    fetchThingsOfInterest
} from './listingExtended';
import {fetchUserData, fetchUserDataAndSetLoggedIn} from './userData';
import {setActiveNavTab} from './navigation';
import {
    registerStickyPosition,
    windowSizeChanged,
    windowScrolled,
    reloadPage
} from './windowHandler';
import {contactOwnerFormFieldEdited, contactOwnerFormFieldTouched} from './contactOwnerForm';
import {syncModalStateWithRoute, navigate, updateUrlAfterModalChange} from './router';
import primaryCTAClicked from './primaryCta';

import {registerTripBoardsMiddleware} from '@homeaway/react-trip-board-button';

// List out all middleware functions and the action that they respond to
function registerAllMiddleware() {
    registerMiddleware(routerActions.LOCATION_CHANGED, syncModalStateWithRoute);
    registerMiddleware(routerActions.NAVIGATE, navigate);

    registerMiddleware(carouselActions.SELECT_NEXT, selectNextImage);
    registerMiddleware(carouselActions.SELECT_PREV, selectPrevImage);

    registerMiddleware(chatbotActions.REQUEST_TO_BOOK, chatbotRequestToBook);
    registerMiddleware(chatbotActions.TRIGGER_CHAT_EXIT_TOAST, triggerChatExitToast);

    registerMiddleware(vtHelpfulnessActions.TRIGGER_VT_EXIT_TOAST, triggerVTExitToast);

    registerMiddleware(listingActions.LOG_TO_SERVER, logToServer);
    registerMiddleware(listingActions.PAGE_LOADED, fetchUnitSpaces);
    registerMiddleware(listingActions.PAGE_LOADED, pageLoadedFetchRegistrationNumber);
    registerMiddleware(listingActions.PAGE_LOADED, pageLoadedFetchGeoDistance);
    registerMiddleware(listingActions.PAGE_LOADED, fetchUserDataAndSetLoggedIn);

    registerMiddleware(listingActions.PAGE_LOADED, fetchDiscoveryFeed);
    registerMiddleware(listingActions.PAGE_LOADED, fetchThingsOfInterest);

    registerMiddleware(listingPageActions.PRIMARY_ACTION_RENDERED, logPrimaryActionRendered);

    registerMiddleware(listingExtendedActions.FETCH_BY_OWNER_PROPERTIES, fetchByOwnerProperties);
    registerMiddleware(
        listingExtendedActions.FETCH_RECOMMENDED_PROPERTIES,
        fetchRecommendedProperties
    );
    registerMiddleware(listingExtendedActions.FETCH_INQUIRY_PROPERTIES, fetchInquiryProperties);

    registerMiddleware(listingActions.SET_TRIP_COOKIE, updateTripDataCookie);
    registerMiddleware(listingActions.PRIMARY_CTA_ACTIVATED, primaryCTAClicked);

    registerMiddleware(windowActions.SET_STICKY_POSITION, registerStickyPosition);
    registerMiddleware(windowActions.WINDOW_SIZE_CHANGED, windowSizeChanged);
    registerMiddleware(windowActions.WINDOW_SCROLLED, windowScrolled);
    registerMiddleware(windowActions.WINDOW_RELOAD, reloadPage);

    registerMiddleware(reviewsActions.VIEW_ALL_REVIEWS, viewAllReviews);
    registerMiddleware(reviewsActions.PREV_PAGE, previousPage);
    registerMiddleware(reviewsActions.NEXT_PAGE, nextPage);
    registerMiddleware(reviewsActions.TRANSLATE_REVIEWS, translateReviews);
    registerMiddleware(reviewsActions.SHOW_ORIGINAL_REVIEWS, showOriginalReviews);

    registerMiddleware(edapActions.TRIGGER_EDAP_EVENT, triggerEdapEvent);
    registerMiddleware(
        edapActions.TRIGGER_EDAP_LISTING_BOOKNOW_CLICK,
        triggerEdapListingBooknowClick
    );

    registerMiddleware(edapActions.TRIGGER_EDAP_CHECKOUT_SELECTED, triggerEdapCheckoutSelected);
    registerMiddleware(
        edapActions.TRIGGER_EDAP_PRICE_QUOTE_PRESENTED,
        triggerEdapPriceQuotePresented
    );
    registerMiddleware(
        edapActions.TRIGGER_EDAP_PROPERTY_SUMMARY_PRESENTED,
        triggerEdapPropertySummaryPresented
    );
    registerMiddleware(edapActions.LOG_TEST_EXPOSURE, logTestExposure);

    registerMiddleware(datePickerActions.SUBMIT_SELECTED_DATES, selectedDatesSubmit);
    registerMiddleware(datePickerActions.CHANGED_SELECTED_DATES, changedSelectedDates);

    registerMiddleware(guestSelectorActions.GUEST_SELECTOR_CHANGED, guestSelectorChanged);
    registerMiddleware(guestSelectorActions.SUBMIT_GUEST_SELECTOR, submitGuestSelectorModal);

    registerMiddleware(tripActions.TRIP_DETAILS_CHANGED, tripDetailsChanged);
    registerMiddleware(
        tripActions.TRIP_DETAILS_CHANGED,
        updateLodgingCancellationPolicyIfArrivalDateChanged
    );

    registerMiddleware(listingActions.OPEN_DATE_PICKER_MODAL, openDatePickerModal);
    registerMiddleware(listingActions.CLOSE_DATE_PICKER_MODAL, closeDatePickerModal);
    registerMiddleware(listingActions.OPEN_GUEST_SELECTOR_MODAL, openGuestSelectorModal);
    registerMiddleware(listingActions.CLOSE_GUEST_SELECTOR_MODAL, closeGuestSelectorModal);
    registerMiddleware(listingActions.REQUEST_TO_BOOK_MODAL_OPEN, setRequestToBookModalOpen);
    registerMiddleware(listingActions.INQUIRY_MODAL_OPEN, setInquiryModalOpen);
    registerMiddleware(
        listingActions.CIVIL_CODE_DISCLAIMER_MODAL_OPEN,
        setCivilCodeDisclaimerModalOpen
    );
    registerMiddleware(listingActions.LOGIN_MODAL_OPEN, setLoginModalOpen);
    registerMiddleware(listingActions.INQUIRY_LOGIN_MODAL_OPEN, setInquiryLoginModalOpen);
    registerMiddleware(listingActions.DESCRIPTION_MODAL_OPEN, setDescriptionModalOpen);
    registerMiddleware(listingActions.UNIT_DESCRIPTION_MODAL_OPEN, setUnitDescriptionModalOpen);
    registerMiddleware(
        listingActions.GENERATED_DESCRIPTION_MODAL_OPEN,
        setGeneratedDescriptionModalOpen
    );
    registerMiddleware(listingActions.AMENITY_MODAL_OPEN, setAmenityModalOpen);
    registerMiddleware(listingActions.OWNER_PROFILE_MODAL_OPEN, setOwnerProfileModalOpen);
    registerMiddleware(listingActions.LOCATION_MODAL_OPEN, setLocationModalOpen);
    registerMiddleware(listingActions.MAP_MODAL_OPEN, setMapModalOpen);
    registerMiddleware(listingActions.REVIEWS_MODAL_OPEN, setReviewsModalOpen);
    registerMiddleware(listingActions.SEARCH_MODAL_OPEN, setSearchModalOpen);
    registerMiddleware(
        listingActions.SEARCH_GUEST_PICKER_MODAL_OPEN,
        setSearchGuestPickerModalOpen
    );
    registerMiddleware(listingActions.SEARCH_DATE_PICKER_MODAL_OPEN, setSearchDatePickerModalOpen);
    registerMiddleware(listingActions.VIRTUAL_TOUR_MODAL_OPEN, setVirtualTourModalOpen);
    registerMiddleware(listingActions.VERTICAL_GALLERY_MODAL_OPEN, setVerticalGalleryModalOpen);
    registerMiddleware(listingActions.IMAGE_CAROUSEL_MODAL_OPEN, setImageCarouselModalOpen);
    registerMiddleware(listingActions.TOI_MODAL_OPEN, setToiModalOpen);
    registerMiddleware(listingActions.UPDATE_MODAL_STATE, updateUrlAfterModalChange, 'after');

    registerMiddleware(listingExtendedActions.FETCH_URGENCY_MESSAGE, fetchUrgencyMessage);

    registerMiddleware(
        contactOwnerFormActions.CONTACT_OWNER_FORM_FIELD_EDITED,
        contactOwnerFormFieldEdited
    );
    registerMiddleware(
        contactOwnerFormActions.CONTACT_OWNER_FORM_FIELD_TOUCHED,
        contactOwnerFormFieldTouched
    );
    registerMiddleware(inquiryModalActions.INQUIRY_SUBMIT, inquirySubmit);
    registerMiddleware(inquiryModalActions.REQUEST_TO_BOOK, inquiryModalRequestToBook);
    registerMiddleware(requestToBookModalActions.BOOKING_SUBMIT, requestToBookModalSubmit);

    registerMiddleware(tripActions.TRIP_DETAILS_EDAP_CHECK, tripDetailsEdapCheck);

    registerMiddleware(listingPageActions.TRIGGER_URGENCY_TOAST, triggerUrgencyToast);
    registerMiddleware(listingPageActions.VIEW_PRICE_DETAILS_CLICKED, viewPriceDetailsClicked);
    registerMiddleware(listingPageActions.INQUIRY_CLICKED, inquiryClicked);
    registerMiddleware(navigationActions.NAV_CLICKED, navClicked);
    registerMiddleware(
        listingPageActions.CIVIL_CODE_DISCLAIMER_CLICKED,
        civilCodeDisclaimerClicked
    );
    registerMiddleware(
        listingPageActions.PROCESS_SERVER_SIDE_EVALUATED_TESTS,
        processServerSideEvaluatedTests
    );
    registerMiddleware(navigationActions.DETERMINE_ACTIVE_NAV_TAB, setActiveNavTab);

    registerMiddleware(listingPageActions.LOG_PRICE_SUMMARY_EVENT, logPriceSummaryEvent);

    registerMiddleware(travelerActions.USER_LOGGED_IN, fetchUserData);
    registerMiddleware(
        listingPageActions.TRANSLATE_PROPERTY_DESCRIPTION,
        fetchPropertyDescriptionLocalized
    );

    registerTripBoardsMiddleware(registerMiddleware);

    registerMiddleware(inquiryModalActions.INQUIRY_APPLINK_UPDATE, inquiryPostApplink);
}

export default registerAllMiddleware;
